import React, { Fragment, FunctionComponent } from 'react';
import { EditOutlined } from '@mui/icons-material';
import { Typography } from '@mui/material';
import i18next from 'i18next';

import { history } from 'helpers/history';
import { getCountryName } from 'helpers/countries';

import {
  ViewContainer,
  ViewContent,
  TitleContainer,
  Title,
  TableContainer,
  CustomIconButton,
  FilePreview,
} from 'styles/view';

import { IMarriagesShowProps } from './types';
import './i18n';

const MarriagesShow: FunctionComponent<IMarriagesShowProps> = (props: IMarriagesShowProps) => {
  const { item, showTitle = true } = props;

  return (
    <ViewContainer auth data-testid="marriage-show">
      <ViewContent auth>
        {showTitle && (
          <TitleContainer>
            <div />
            <Title>{i18next.t<string>('MARRIAGES_SHOW:TITLE')}</Title>
            <CustomIconButton
              onClick={() => history.push(`/public/procedures/forms/marriages/${item?.id}/edit`)}
            >
              <EditOutlined />
            </CustomIconButton>
          </TitleContainer>
        )}
        {item && (
          <>
            {['groom', 'bride'].map((value) => {
              const person = item[value as 'groom' | 'bride'];
              return (
                <Fragment key={`${value}-information`}>
                  <Typography variant="h6">
                    {i18next.t<string>(`MARRIAGES_SHOW:${value.toUpperCase()}_INFORMATION`)}
                  </Typography>
                  <br />
                  <TableContainer>
                    <tbody>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:LAST_NAME')}</td>
                        <td>{person.lastName}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:FIRST_NAME')}</td>
                        <td>{person.firstName}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:ID_CARD_TYPE')}</td>
                        <td>{person.idCardTypeName}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:ID_CARD')}</td>
                        <td>{person.idCard}</td>
                      </tr>
                      {person.idCardFile && (
                        <tr>
                          <td>{i18next.t<string>('MARRIAGES_SHOW:ID_CARD_FILE')}</td>
                          <td>
                            <FilePreview src={person.idCardFile} title="idCardFile" />
                          </td>
                        </tr>
                      )}
                      {person.idCardBackFile && (
                        <tr>
                          <td>{i18next.t<string>('MARRIAGES_SHOW:ID_CARD_BACK_FILE')}</td>
                          <td>
                            <FilePreview src={person.idCardBackFile} title="idCardBackFile" />
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:CIVIL_STATE')}</td>
                        <td>{person.civilStateName}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:TRIBE')}</td>
                        <td>{person.tribeName}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:ADDRESS')}</td>
                        <td>{person.address}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:APARTMENT')}</td>
                        <td>{person.apartment}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:LOCALITY')}</td>
                        <td>{person.locality}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:COUNTRY')}</td>
                        <td>{getCountryName(person.country)}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:BIRTHPLACE')}</td>
                        <td>{person.birthplace}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:BIRTHDATE')}</td>
                        <td>{person.birthdate}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:EMAIL')}</td>
                        <td>{person.email}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:PHONE')}</td>
                        <td>{`${person.areaCode ? `(${person.areaCode}) ` : ''}${
                          person.phone
                        }`}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:COMMUNITY')}</td>
                        <td>{person.community}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:JOB')}</td>
                        <td>{person.job}</td>
                      </tr>
                      {person.getFile && (
                        <tr>
                          <td>{i18next.t<string>('MARRIAGES_SHOW:GET_FILE')}</td>
                          <td>
                            <FilePreview src={person.getFile} title="getFile" />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </TableContainer>
                </Fragment>
              );
            })}
            {['groom', 'bride'].map((value) => {
              const person = item[value as 'groom' | 'bride'];
              return (
                <Fragment key={`${value}-information`}>
                  <Typography variant="h6">
                    {i18next.t<string>(`MARRIAGES_SHOW:${value.toUpperCase()}_PARENTS_INFORMATION`)}
                  </Typography>
                  <br />
                  <TableContainer>
                    <tbody>
                      {['father', 'mother'].map((parentValue) => {
                        const parent = person.parents[parentValue as 'father' | 'mother'];
                        return (
                          <Fragment key={`${parentValue}-parents-information`}>
                            <tr>
                              <td>
                                {i18next.t<string>(
                                  `MARRIAGES_SHOW:${parentValue.toUpperCase()}_LAST_NAME`,
                                )}
                              </td>
                              <td>{parent.lastName}</td>
                            </tr>
                            <tr>
                              <td>
                                {i18next.t<string>(
                                  `MARRIAGES_SHOW:${parentValue.toUpperCase()}_NAME`,
                                )}
                              </td>
                              <td>{parent.name}</td>
                            </tr>
                            <tr>
                              <td>{i18next.t<string>('MARRIAGES_SHOW:ID_CARD_TYPE')}</td>
                              <td>{parent.idCardTypeName}</td>
                            </tr>
                            <tr>
                              <td>{i18next.t<string>('MARRIAGES_SHOW:ID_CARD')}</td>
                              <td>{parent.idCard}</td>
                            </tr>
                            {parent.idCardFile && (
                              <tr>
                                <td>{i18next.t<string>('MARRIAGES_SHOW:ID_CARD_FILE')}</td>
                                <td>
                                  <FilePreview src={parent.idCardFile} title="idCardFile" />
                                </td>
                              </tr>
                            )}
                            {parent.idCardBackFile && (
                              <tr>
                                <td>{i18next.t<string>('MARRIAGES_SHOW:ID_CARD_BACK_FILE')}</td>
                                <td>
                                  <FilePreview src={parent.idCardBackFile} title="idCardBackFile" />
                                </td>
                              </tr>
                            )}
                            <tr>
                              <td>{i18next.t<string>('MARRIAGES_SHOW:BIRTHPLACE')}</td>
                              <td>{parent.birthplace}</td>
                            </tr>
                            <tr>
                              <td>{i18next.t<string>('MARRIAGES_SHOW:BIRTHDATE')}</td>
                              <td>{parent.birthdate}</td>
                            </tr>
                            <tr>
                              <td>{i18next.t<string>('MARRIAGES_SHOW:CIVIL_STATE')}</td>
                              <td>{parent.civilStateName}</td>
                            </tr>
                            {parent.getFile && (
                              <tr>
                                <td>{i18next.t<string>('MARRIAGES_SHOW:GET_FILE')}</td>
                                <td>
                                  <FilePreview src={parent.getFile} title="getFile" />
                                </td>
                              </tr>
                            )}
                            <tr>
                              <td>{i18next.t<string>('MARRIAGES_SHOW:TRIBE')}</td>
                              <td>{parent.tribeName}</td>
                            </tr>
                            <tr>
                              <td>{i18next.t<string>('MARRIAGES_SHOW:ALIVE')}</td>
                              <td>
                                {parent.alive
                                  ? i18next.t<string>('MARRIAGES_SHOW:YES')
                                  : i18next.t<string>('MARRIAGES_SHOW:NO')}
                              </td>
                            </tr>
                            <tr>
                              <td>{i18next.t<string>('MARRIAGES_SHOW:CEMENTERY')}</td>
                              <td>
                                {parent.alive ? '-' : parent.cementeryName || parent.cementery}
                              </td>
                            </tr>
                          </Fragment>
                        );
                      })}
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:WEDDING_TEMPLE')}</td>
                        <td>{person.parents.weddingTemple}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:WEDDING_DATE')}</td>
                        <td>{person.parents.weddingDate}</td>
                      </tr>
                      {person.parents.ketubaFile && (
                        <tr>
                          <td>{i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE')}</td>
                          <td>
                            <FilePreview src={person.parents.ketubaFile} title="ketubaFile" />
                          </td>
                        </tr>
                      )}
                      {person.parents.marriageCertificateFile && (
                        <tr>
                          <td>{i18next.t<string>('MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE')}</td>
                          <td>
                            <FilePreview
                              src={person.parents.marriageCertificateFile}
                              title="marriageCertificateFile"
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </TableContainer>
                </Fragment>
              );
            })}
            {['groom', 'bride'].map((value) => {
              const person = item[value as 'groom' | 'bride'];
              return (
                <Fragment key={`${value}-grand-parents-information`}>
                  <Typography variant="h6">
                    {i18next.t<string>(
                      `MARRIAGES_SHOW:${value.toUpperCase()}_GRANDPARENTS_INFORMATION`,
                    )}
                  </Typography>
                  <br />
                  <TableContainer>
                    <tbody>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:PATERNAL_FATHER_NAME')}</td>
                        <td>{person.grandParents.paternalFather.name}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:PATERNAL_FATHER_LAST_NAME')}</td>
                        <td>{person.grandParents.paternalFather.lastName}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:BIRTHPLACE')}</td>
                        <td>{person.grandParents.paternalFather.birthplace}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:PATERNAL_MOTHER_NAME')}</td>
                        <td>{person.grandParents.paternalMother.name}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:PATERNAL_MOTHER_LAST_NAME')}</td>
                        <td>{person.grandParents.paternalMother.lastName}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:BIRTHPLACE')}</td>
                        <td>{person.grandParents.paternalMother.birthplace}</td>
                      </tr>
                      {person.grandParents.paternalKetubaFile && (
                        <tr>
                          <td>{i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE')}</td>
                          <td>
                            <FilePreview
                              src={person.grandParents.paternalKetubaFile}
                              title="ketubaFile"
                            />
                          </td>
                        </tr>
                      )}
                      {person.grandParents.paternalMarriageCertificateFile && (
                        <tr>
                          <td>{i18next.t<string>('MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE')}</td>
                          <td>
                            <FilePreview
                              src={person.grandParents.paternalMarriageCertificateFile}
                              title="marriageCertificateFile"
                            />
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:MATERNAL_FATHER_NAME')}</td>
                        <td>{person.grandParents.maternalFather.name}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:MATERNAL_FATHER_LAST_NAME')}</td>
                        <td>{person.grandParents.maternalFather.lastName}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:BIRTHPLACE')}</td>
                        <td>{person.grandParents.maternalFather.birthplace}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:MATERNAL_MOTHER_NAME')}</td>
                        <td>{person.grandParents.maternalMother.name}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:MATERNAL_MOTHER_LAST_NAME')}</td>
                        <td>{person.grandParents.maternalMother.lastName}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('MARRIAGES_SHOW:BIRTHPLACE')}</td>
                        <td>{person.grandParents.maternalMother.birthplace}</td>
                      </tr>
                      {person.grandParents.maternalKetubaFile && (
                        <tr>
                          <td>{i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE')}</td>
                          <td>
                            <FilePreview
                              src={person.grandParents.maternalKetubaFile}
                              title="ketubaFile"
                            />
                          </td>
                        </tr>
                      )}
                      {person.grandParents.maternalMarriageCertificateFile && (
                        <tr>
                          <td>{i18next.t<string>('MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE')}</td>
                          <td>
                            <FilePreview
                              src={person.grandParents.maternalMarriageCertificateFile}
                              title="marriageCertificateFile"
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </TableContainer>
                </Fragment>
              );
            })}
            {['groom', 'bride'].map((value) => {
              const person = item[value as 'groom' | 'bride'];
              return (
                <Fragment key={`${value}-witnesses-information`}>
                  <Typography variant="h6">
                    {i18next.t<string>(
                      `MARRIAGES_SHOW:${value.toUpperCase()}_WITNESSES_INFORMATION`,
                    )}
                  </Typography>
                  <br />
                  <TableContainer>
                    <tbody>
                      {person.witnesses.map((witness, i) => (
                        <Fragment key={`${witness}-${i.toString()}-information`}>
                          <tr>
                            <td>{`${i18next.t<string>('MARRIAGES_SHOW:WITNESS_NAME')} ${
                              i + 1
                            }`}</td>
                            <td>{witness.name}</td>
                          </tr>
                          <tr>
                            <td>{i18next.t<string>('MARRIAGES_SHOW:EMAIL')}</td>
                            <td>{witness.email}</td>
                          </tr>
                          <tr>
                            <td>{i18next.t<string>('MARRIAGES_SHOW:ID_CARD_TYPE')}</td>
                            <td>{witness.idCardTypeName}</td>
                          </tr>
                          <tr>
                            <td>{i18next.t<string>('MARRIAGES_SHOW:ID_CARD')}</td>
                            <td>{witness.idCard}</td>
                          </tr>
                          {witness.idCardFile && (
                            <tr>
                              <td>{i18next.t<string>('MARRIAGES_SHOW:ID_CARD_FILE')}</td>
                              <td>
                                <FilePreview src={witness.idCardFile} title="idCardFile" />
                              </td>
                            </tr>
                          )}
                          {witness.idCardBackFile && (
                            <tr>
                              <td>{i18next.t<string>('MARRIAGES_SHOW:ID_CARD_BACK_FILE')}</td>
                              <td>
                                <FilePreview src={witness.idCardBackFile} title="idCardBackFile" />
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td>{i18next.t<string>('MARRIAGES_SHOW:ADDRESS')}</td>
                            <td>{witness.address}</td>
                          </tr>
                          <tr>
                            <td>{i18next.t<string>('MARRIAGES_SHOW:PHONE')}</td>
                            <td>{`${witness.areaCode ? `(${witness.areaCode}) ` : ''}${
                              witness.phone
                            }`}</td>
                          </tr>
                          {witness.ketubaFile && (
                            <tr>
                              <td>{i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE')}</td>
                              <td>
                                <FilePreview src={witness.ketubaFile} title="ketubaFile" />
                              </td>
                            </tr>
                          )}
                          {witness.marriageCertificateFile && (
                            <tr>
                              <td>
                                {i18next.t<string>('MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE')}
                              </td>
                              <td>
                                <FilePreview
                                  src={witness.marriageCertificateFile}
                                  title="marriageCertificateFile"
                                />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                  </TableContainer>
                </Fragment>
              );
            })}
            <Typography variant="h6">
              {i18next.t<string>('MARRIAGES_SHOW:CEREMONY_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('MARRIAGES_SHOW:ADDRESS')}</td>
                  <td>{item.ceremony.address}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('MARRIAGES_SHOW:LOCALITY')}</td>
                  <td>{item.ceremony.locality}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('MARRIAGES_SHOW:CEREMONY_RELIGIOUS_DATE')}</td>
                  <td>{item.ceremony.religiousDate}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('MARRIAGES_SHOW:CEREMONY_CIVIL_DATE')}</td>
                  <td>{item.ceremony.civilDate}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('MARRIAGES_SHOW:CEREMONY_OFFICIANT')}</td>
                  <td>{item.ceremony.officiant}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('MARRIAGES_SHOW:CEREMONY_CIVIL_LOCALITY')}</td>
                  <td>{item.ceremony.civilLocality}</td>
                </tr>
              </tbody>
            </TableContainer>
            {item.comments && (
              <>
                <Typography variant="h6">{i18next.t<string>('MARRIAGES_SHOW:COMMENTS')}</Typography>
                <br />
                {item.comments}
              </>
            )}
          </>
        )}
      </ViewContent>
    </ViewContainer>
  );
};

export default MarriagesShow;
